/**
 * A single-select filter (TMMAPP-0088, pattern 3). The track is opaque `surface`, so the selected
 * segment (`selected` layer + `link` text) is the guaranteed `link / surface under selected` pair.
 * `link` on `bg` under `selected` is not a saas-auth guarantee, so this control does not paint it.
 * A null change (clicking the selected segment) is ignored — the choice cannot clear.
 */
import type { MouseEvent } from 'react';
import { ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { labelCanvasSx } from '@/theme/textLabel';
import { resolveTokens } from '@/theme/theme';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** An element describing this segment (a subscriber-only choice's hint). */
  describedBy?: string | undefined;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: readonly SegmentedOption<T>[];
  /** The segment's click comes along, for a choice that runs through `useSubscriberAction()`. */
  onChange: (value: T, event: MouseEvent<HTMLElement>) => void;
  'aria-label': string;
  /** The settings toggle's padding 8 20 (P:1752). */
  wide?: boolean;
  /** The filter toggle (visual contract 5): padding 6 14, 12/500, sentence case (contract v2 §7). */
  filter?: boolean;
  /**
   * A choice is saving (`aria-busy`). The segments stay enabled, so focus stays on the one pressed;
   * the host ignores a choice while busy.
   */
  busy?: boolean;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
  wide = false,
  filter = false,
  busy = false,
}: SegmentedControlProps<T>) {
  const tokens = resolveTokens(useTheme());
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      onChange={(event, next: T | null) => {
        if (next !== null) onChange(next, event);
      }}
      sx={{ bgcolor: tokens.surface, ...labelCanvasSx(tokens, 'surface') }}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-describedby={option.describedBy}
          sx={{
            color: tokens.text,
            ...(wide ? { padding: '8px 20px' } : {}),
            ...(filter
              ? {
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.5,
                }
              : {}),
            '&.Mui-selected': {
              backgroundColor: tokens.selected,
              color: tokens.link,
              '&:hover': {
                backgroundColor: tokens.selected,
                color: tokens.link,
              },
            },
          }}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
